import React from 'react';
import { motion } from 'framer-motion';
import { Phone, MessageCircle, Mail, MapPin, ArrowUpRight } from 'lucide-react';
import { COMPANY_INFO, NAV_LINKS } from '../mock';
import { getVersionString } from '../config/version';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleOpenCookieSettings = () => {
    window.dispatchEvent(new CustomEvent('openCookieSettings'));
  };

  const geoLinks = [
    { label: 'Rellingen (Hauptsitz)', href: '/unfallgutachten-rellingen' },
    { label: 'Hamburg', href: '/unfallgutachten-hamburg' },
    { label: 'Norderstedt', href: '/unfallgutachten-norderstedt' },
    { label: 'Pinneberg', href: '/unfallgutachten-pinneberg' },
    { label: 'Mainz', href: '/unfallgutachten-mainz' },
    { label: 'Frankfurt', href: '/unfallgutachten-frankfurt' },
  ];

  const ratgeberLinks = [
    { label: 'Nach dem Unfall', href: '/ratgeber/nach-unfall-checkliste' },
    { label: 'Wertminderung', href: '/ratgeber/wertminderung' },
    { label: 'Gutachten vs. KVA', href: '/ratgeber/gutachten-oder-kostenvoranschlag' },
  ];

  return (
    <footer className="relative bg-[#0f172a] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#22c55e]/5 rounded-full blur-[100px]" />
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#22c55e]/5 rounded-full blur-[80px]" />
      </div>

      {/* Main Footer Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          {/* Column 1: Brand */}
          <div className="lg:col-span-1">
            <a href="/" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png" 
                alt="Dein Gutachten Direkt Logo" 
                className="h-10 w-auto"
              />
            </a>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Unabhängige Unfallgutachten durch erfahrene Sachverständige – bundesweit direkt vor Ort.
            </p>
            {/* CTA Button */}
            <a 
              href={`tel:${COMPANY_INFO.phone}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#22c55e]/25"
            >
              <Phone className="w-4 h-4" />
              Jetzt anrufen
            </a>
          </div>

          {/* Column 2: Seiten */}
          <div>
            <h3 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Seiten</h3>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href} 
                    className="text-slate-400 hover:text-[#22c55e] transition-colors text-sm inline-flex items-center gap-1 group"
                  >
                    {link.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-white/10">
              <a 
                href="/portal/login" 
                className="text-[#22c55e] hover:text-[#4ade80] transition-colors text-sm font-semibold inline-flex items-center gap-2"
              >
                Partner Login
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Column 3: Standorte & Ratgeber */}
          <div>
            <h3 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Standorte</h3>
            <ul className="space-y-2 mb-6">
              {geoLinks.map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href} 
                    className="text-slate-400 hover:text-[#22c55e] transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Ratgeber</h4>
            <ul className="space-y-2">
              {ratgeberLinks.map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href} 
                    className="text-slate-400 hover:text-[#22c55e] transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Kontakt */}
          <div>
            <h3 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Kontakt</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${COMPANY_INFO.phone}`} 
                  className="flex items-center gap-3 text-slate-400 hover:text-[#22c55e] transition-colors group"
                >
                  <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-[#22c55e]/10 transition-colors">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-0.5">Telefon</p>
                    <p className="text-sm font-medium">Kostenlos anrufen</p>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href={`https://wa.me/${COMPANY_INFO.whatsapp}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-[#22c55e] transition-colors group"
                >
                  <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-[#22c55e]/10 transition-colors">
                    <MessageCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-0.5">WhatsApp</p>
                    <p className="text-sm font-medium">Chat starten</p>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${COMPANY_INFO.email}`} 
                  className="flex items-center gap-3 text-slate-400 hover:text-[#22c55e] transition-colors group"
                >
                  <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-[#22c55e]/10 transition-colors">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-0.5">E-Mail</p>
                    <p className="text-sm font-medium">Nachricht senden</p>
                  </div>
                </a>
              </li>
            </ul>
            
            {/* Einsatzgebiet */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center gap-3 text-slate-400">
                <div className="w-10 h-10 bg-[#22c55e]/10 rounded-xl flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#22c55e]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">Einsatzgebiet</p>
                  <p className="text-sm font-semibold text-white">Bundesweit</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-slate-500 text-sm">
              © {currentYear} {COMPANY_INFO.name}
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              <a href="/impressum" className="text-slate-500 hover:text-[#22c55e] text-sm transition-colors">
                Impressum
              </a>
              <a href="/datenschutz" className="text-slate-500 hover:text-[#22c55e] text-sm transition-colors">
                Datenschutz
              </a>
              <a href="/agb" className="text-slate-500 hover:text-[#22c55e] text-sm transition-colors">
                AGB
              </a>
              <button
                onClick={handleOpenCookieSettings}
                className="text-slate-500 hover:text-[#22c55e] text-sm transition-colors"
              >
                Cookie-Einstellungen
              </button>
              <span className="text-slate-600 text-xs">
                {getVersionString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};